import { Controller } from "@hotwired/stimulus"
import { createChart, LineSeries, LineStyle } from "lightweight-charts"
import { buildChartOptions } from "../utils/chart_utils"

// The time-weighted return chart: N labelled lines, each a cumulative return
// as a percentage rebased to zero on the window's first day. The Performance
// tab draws one portfolio plus its benchmark; the overview draws every
// portfolio plus a shared benchmark. Same payload either way — a controller
// shaped around exactly "portfolio + benchmark" would have needed a
// near-identical twin for the second caller.
//
// The data arrives in the page as a Stimulus value rather than over a fetch:
// the server has already computed every line, the window is bounded by the
// period buttons, and a chart the page already holds should not make the
// member watch a spinner. The period buttons are links, so changing the window
// is a navigation and this controller only ever draws once.
//
// lightweight-charts is in the main bundle (14 other views use it), so nothing
// is lazily loaded here. It holds a canvas and a ResizeObserver that are not
// garbage-collected on their own — disconnect() must tear both down or a Turbo
// navigation leaks them.
//
// CSP-safe: no inline handlers, no eval.

// The colour a line gets when the server names none. A muted grey rather than
// a second bright hue: the only uncoloured line is the benchmark, which is the
// reference the eye compares against, and two equally loud lines make the
// reader work out which is which every time. Neutral enough to read in either
// theme, which is why it is a literal and not a themed colour — and it lives
// here rather than in each caller so "the reference line is grey" has one home.
const FALLBACK_COLOR = "#9aa0a6"

// The two styles a line may name, mapped onto the library's own enum rather
// than onto the integers behind it — a bare `2` in the series options reads
// as a width.
const LINE_STYLES = { solid: LineStyle.Solid, dashed: LineStyle.Dashed }

export default class extends Controller {
  static targets = ["canvas"]
  static values = {
    // [{ label, color, style: "solid"|"dashed", width, data: [{time, value}] }]
    series: { type: Array, default: [] }
  }

  connect() {
    this.chart = null
    this.resizeObserver = null
    this.onThemeChanged = () => this.applyTheme()

    this.draw()
  }

  disconnect() {
    document.removeEventListener("theme:changed", this.onThemeChanged)

    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    if (this.chart) {
      this.chart.remove()
      this.chart = null
    }
  }

  draw() {
    // Read once: a Stimulus array value is re-parsed from the attribute on
    // every access, and this payload is every line on the chart.
    const series = this.seriesValue.filter((line) => line.data && line.data.length > 0)
    // Nothing to draw is not an error: the server renders its own empty state
    // above this element, and building an empty chart would paint an axis
    // over it.
    if (series.length === 0) return

    const container = this.canvasTarget
    // Turbo snapshots the DOM for its cache BEFORE controllers disconnect, so
    // a cached copy of this page still holds the canvases lightweight-charts
    // appended. Without this, a back-navigation draws a second chart on top of
    // the stale one. It also clears the no-JavaScript fallback below.
    container.innerHTML = ""

    this.chart = createChart(container, this.chartOptions())

    series.forEach((line) => {
      this.chart.addSeries(LineSeries, {
        color: line.color || FALLBACK_COLOR,
        lineWidth: line.width || 2,
        lineStyle: LINE_STYLES[line.style] ?? LINE_STYLES.solid,
        title: line.label || "",
        priceLineVisible: false
      }).setData(line.data)
    })

    this.chart.timeScale().fitContent()

    this.resizeObserver = new ResizeObserver(() => {
      if (!this.chart) return
      this.chart.applyOptions({ width: container.clientWidth || 800 })
    })
    this.resizeObserver.observe(container)

    document.addEventListener("theme:changed", this.onThemeChanged)
  }

  // The shared builder, so this chart's fonts, date labels and bar spacing
  // match every other lightweight chart on the site rather than drifting into
  // its own look.
  //
  // Every line is a percentage of the same kind, so one formatter serves the
  // axis, the crosshair and the legend.
  chartOptions() {
    const container = this.canvasTarget

    return {
      ...buildChartOptions({
        width: container.clientWidth || 800,
        height: container.clientHeight || 360,
        bottomMargin: 0.1
      }),
      localization: { priceFormatter: (value) => `${value.toFixed(2)}%` }
    }
  }

  // Called on theme:changed. COLOURS ONLY — re-applying the full option set
  // would carry `barSpacing` / `rightOffset` / `width` with it and snap a
  // member who had zoomed or panned back to the default view just because they
  // flipped light/dark. Same split as lightweight_chart_controller's
  // applyChartTheme.
  //
  // The values are picked out of `buildChartOptions()` rather than re-listed:
  // CHART_COLORS is a live proxy over the CSS variables, so calling it again
  // resolves the new theme. The lines keep their own colours, all of which
  // read in either theme.
  applyTheme() {
    if (!this.chart) return

    const themed = buildChartOptions()
    this.chart.applyOptions({
      layout: themed.layout,
      grid: themed.grid,
      timeScale: { borderColor: themed.timeScale.borderColor },
      rightPriceScale: { borderColor: themed.rightPriceScale.borderColor }
    })
  }
}
